import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Avatar, Badge, Typography, TextField, IconButton, Grow, Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GroupIcon from '@mui/icons-material/Group';
import { useChat } from '../../contexts/ChatContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatMessageItem from './ChatMessageItem';

// Draggable bubble wrapper
const DraggableBubble = ({ defaultTop, defaultLeft, children, onClick }) => {
  const [pos, setPos] = useState({ x: defaultLeft, y: defaultTop });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const moved = useRef(false);
  const ref = useRef(null);

  const handleMouseDown = useCallback((e) => {
    dragging.current = true;
    moved.current = false;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragging.current) return;
      moved.current = true;
      setPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
    };
    const handleMouseUp = () => {
      dragging.current = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleClick = () => {
    if (!moved.current) onClick();
  };

  return (
    <Box
      ref={ref}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      sx={{
        position: 'fixed',
        top: pos.y,
        left: pos.x,
        zIndex: 1300,
        cursor: dragging.current ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
    >
      {children}
    </Box>
  );
};

const accent = '#7C3AED';

const windowStyle = {
  width: 350,
  height: 450,
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const ChatWindow = ({ convId, index }) => {
  const {
    conversations, messagesCache, loadMessages,
    sendMessage, markAsRead, closeBubbleWindow, minimizeWindow, myId
  } = useChat();
  const navigate = useNavigate();

  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  const conv = conversations.find(c => c.id === convId);
  const messages = (messagesCache[convId] || []).slice(-20);

  useEffect(() => {
    if (!messagesCache[convId]) {
      loadMessages(convId);
    }
    markAsRead(convId);
  }, [convId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const getConvName = (c) => {
    if (!c) return 'Chat';
    if (c.conv_type === 'direct') return c.dm_peer?.name || 'Chat';
    return c.name || 'Grupo';
  };

  const getConvAvatar = (c) => {
    if (!c) return '';
    if (c.conv_type === 'direct') return c.dm_peer?.avatar || '';
    return c.avatar_path || '';
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text) return;
    setInputText('');
    try {
      await sendMessage(convId, text);
    } catch (e) {
      console.error('Send failed:', e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const rightOffset = 16 + index * 366;

  return (
    <Grow in>
      <Box sx={{ position: 'fixed', bottom: 16, right: rightOffset, zIndex: 1300, ...windowStyle }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 1,
          borderBottom: '1px solid #d1d5db',
          background: '#f0f1f3',
        }}>
          <Avatar
            src={getConvAvatar(conv) || undefined}
            sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'rgba(124,58,237,0.12)', color: accent }}
          >
            {conv?.conv_type !== 'direct' ? <GroupIcon sx={{ fontSize: 16 }} /> : (getConvName(conv) || '?')[0]}
          </Avatar>
          <Typography variant="body2" noWrap sx={{ color: '#1a1a2e', flex: 1, fontWeight: 600, fontSize: 13 }}>
            {getConvName(conv)}
          </Typography>
          <Tooltip title="Abrir en chat completo">
            <IconButton
              size="small"
              onClick={() => { closeBubbleWindow(convId); navigate(`/chat?conv=${convId}`); }}
              sx={{ color: '#9ca3af', p: 0.3 }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => minimizeWindow(convId)}
            sx={{ color: '#9ca3af', p: 0.3 }}
          >
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => closeBubbleWindow(convId)}
            sx={{ color: '#9ca3af', p: 0.3 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 0.5, background: '#f0f1f3' }}>
          {messages.map((msg, i) => (
            <ChatMessageItem
              key={msg.id}
              message={msg}
              isOwn={msg.sender_id === myId}
              showAvatar={i === 0 || messages[i - 1]?.sender_id !== msg.sender_id}
              compact
            />
          ))}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.8,
          borderTop: '1px solid #d1d5db',
        }}>
          <TextField
            fullWidth size="small"
            placeholder="Mensaje..."
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: 13, borderRadius: 2,
                background: '#f0f1f3',
                '& fieldset': { borderColor: '#e5e7eb' },
                '&.Mui-focused fieldset': { borderColor: accent }
              },
              '& .MuiOutlinedInput-input': { py: 0.8 }
            }}
          />
          <IconButton
            size="small"
            onClick={handleSend}
            disabled={!inputText.trim()}
            sx={{ color: inputText.trim() ? accent : '#d1d5db' }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Grow>
  );
};

const ChatBubbles = () => {
  const { openBubbles, openWindows, openBubbleWindow, closeBubbleWindow, conversations } = useChat();
  const location = useLocation();

  if (location.pathname === '/chat') return null;

  const getConvName = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    if (!conv) return '?';
    if (conv.conv_type === 'direct') return conv.dm_peer?.name || '?';
    return conv.name || '?';
  };

  const getConvAvatar = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    if (!conv) return '';
    if (conv.conv_type === 'direct') return conv.dm_peer?.avatar || '';
    return conv.avatar_path || '';
  };

  const getConvUnread = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    return conv?.unread_count || 0;
  };

  const isGroup = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    return conv && conv.conv_type !== 'direct';
  };

  const windowsWidth = openWindows.length * 366;
  const bubblesStartRight = 16 + windowsWidth + (openWindows.length > 0 ? 16 : 0);

  return (
    <>
      {openBubbles.map((convId, i) => (
        <DraggableBubble
          key={convId}
          defaultTop={80 + Math.floor(Math.random() * (window.innerHeight - 200))}
          defaultLeft={100 + Math.floor(Math.random() * (window.innerWidth - 300))}
          onClick={() => openBubbleWindow(convId)}
        >
          <Tooltip title={getConvName(convId)} placement="right">
            <Badge
              badgeContent={getConvUnread(convId)}
              color="error"
              overlap="circular"
              sx={{ '& .MuiBadge-badge': { fontSize: 11, minWidth: 18, height: 18 } }}
            >
              <Avatar
                src={getConvAvatar(convId) || undefined}
                sx={{
                  width: 56, height: 56,
                  bgcolor: accent,
                  color: '#fff',
                  border: '3px solid #fff',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                  fontSize: 20,
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(124,58,237,0.4)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(124,58,237,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(124,58,237,0)' },
                  },
                  animation: getConvUnread(convId) > 0 ? 'pulse 2s infinite' : 'none',
                }}
              >
                {isGroup(convId) ? <GroupIcon sx={{ fontSize: 24 }} /> : (getConvName(convId) || '?')[0]}
              </Avatar>
            </Badge>
          </Tooltip>
        </DraggableBubble>
      ))}

      {openWindows.map((convId, i) => (
        <ChatWindow key={convId} convId={convId} index={i} />
      ))}
    </>
  );
};

export default ChatBubbles;
